import type { RxJsonSchema } from 'rxdb'

/**
 * TaskContent Document Type
 * Stores task content separately from metadata to enable:
 * 1. Independent sync (prevents conflicts between content and metadata updates)
 * 2. Future collaboration features (CRDT, OT)
 * 3. Versioning and content history
 *
 * 1:1 relationship with Task (enforced by backend — one content doc per taskId)
 */
export interface TaskContentDocument {
  id: string // Backend content record ID
  taskId: string // References Task.id (1:1 relationship, enforced by backend)
  envId: string
  isDeleted: boolean
  content: string
  format: string // 'tiptap', 'markdown', 'plain'
  length: number
  plainText?: string | null
  version: number // Monotonic version counter for conflict resolution

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number // Required for sync!
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for TaskContent
 */
export const taskContentSchema: RxJsonSchema<TaskContentDocument> = {
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    taskId: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },
    content: {
      type: 'string',
      maxLength: 100000,
      default: '',
    },
    format: {
      type: 'string',
      enum: ['tiptap', 'markdown', 'plain'],
      default: 'tiptap',
    },
    length: {
      type: 'number',
      minimum: 0,
      maximum: 1000000,
      multipleOf: 1,
      default: 0,
    },
    plainText: {
      type: ['string', 'null'],
      maxLength: 100000,
    },
    version: {
      type: 'number',
      minimum: 1,
      maximum: 999999999,
      multipleOf: 1,
      default: 1,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'taskId',
    'envId',
    'isDeleted',
    'content',
    'format',
    'length',
    'version',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'taskId', // Index for querying by task
    'envId',
    'updatedOn', // Critical for sync checkpoint
    ['envId', 'isDeleted'],
    ['taskId', 'isDeleted'], // Query content for a specific task
    ['envId', 'updatedOn'],
  ],
}

export const taskContentMigrationStrategies = {}
